/** Alle Pflanzen, nach Raum gruppiert. */
import { Link } from 'react-router';
import { Plus } from 'lucide-react';
import { fmtDueRelative, type TaskStatus } from '@pflanzen/shared';
import { useStore } from '../lib/store';
import { useNow, useStatuses } from '../lib/hooks';
import { PlantAvatar } from '../ui/TaskRow';
import { Pill, SectionTitle } from '../ui/kit';

export function PlantsPage() {
  const { state } = useStore();
  const now = useNow();
  const statuses = useStatuses(state, now);
  if (!state) return null;
  const plants = state.plants.filter((p) => !p.archivedAt);
  const byPlant = new Map<string, TaskStatus[]>();
  for (const s of statuses) byPlant.set(s.plant.id, [...(byPlant.get(s.plant.id) ?? []), s]);

  return (
    <div className="px-4 pb-6">
      <header className="pt-safe flex items-end justify-between pb-2">
        <div>
          <p className="text-sm text-muted">{plants.length} Pflanzen</p>
          <h1 className="font-display text-[34px] leading-tight">Pflanzen</h1>
        </div>
        <Link to="/pflanzen/neu" aria-label="Pflanze hinzufügen" className="grid h-11 w-11 place-items-center rounded-full bg-moss text-paper active:scale-95">
          <Plus className="h-5 w-5" />
        </Link>
      </header>
      {state.rooms.map((room) => {
        const inRoom = plants.filter((p) => p.roomId === room.id);
        if (inRoom.length === 0) return null;
        return (
          <section key={room.id}>
            <SectionTitle>{room.name}</SectionTitle>
            <div className="grid grid-cols-2 gap-2.5">
              {inRoom.map((p) => {
                const list = byPlant.get(p.id) ?? [];
                const next = list.find((s) => s.daysUntilDue != null);
                const due = list.some((s) => (s.state === 'due' || s.state === 'overdue') && !s.handledToday);
                return (
                  <Link key={p.id} to={`/pflanzen/${p.id}`} className="rise rounded-3xl border border-line bg-card p-3 active:scale-[0.98]">
                    <PlantAvatar photoId={p.coverPhotoId} emoji={p.emoji} />
                    <div className="mt-2 line-clamp-2 text-[15px] leading-tight font-semibold">{p.name}</div>
                    <div className="mt-1">
                      {due ? (
                        <Pill tone="water">heute dran</Pill>
                      ) : next ? (
                        <Pill>{next.type.icon} {fmtDueRelative(next.daysUntilDue ?? 0)}</Pill>
                      ) : (
                        <Pill>alles gut</Pill>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
